/**
 * BaaS card programs via MobileService `/v1/ib/console/*` → BaaS.
 *
 *   POST   /v1/cards/programs              scope cards.write
 *   GET    /v1/cards/programs              scope cards.read
 *   GET    /v1/cards/programs/{program_id} scope cards.read
 *   PATCH  /v1/cards/programs/{program_id} scope cards.write
 *
 * Create / PATCH body (snake_case):
 *   name, ps_product_code (create), network?,
 *   logo_base64? | logo? | logo_url? (http or data URL), logo_content_type?,
 *   front_art_base64?, front_art_content_type?,
 *   back_art_base64?, back_art_content_type?,
 *   art?: { front, back, front_base64, back_base64, … },
 *   verso? {
 *     website, phone_capitals, phone_other,
 *     card_number_preview, expiry_preview, cvc_preview, name_preview
 *   },
 *   authorizer_url?, authorizer_public_key?,
 *   program_kind? (NATIVE|COBRANDED; default NATIVE),
 *   invoice_days? (subset of 5|15|25),
 *   settlement_debit_days? (default 0), settlement_credit_days? (default 19),
 *   guarantee_percent? (policy 0–100, default 100 — not the money),
 *   planned_credit_amount? (major units; default 0 — drives required_guarantee),
 *   webhook_url?, webhook_secret?,
 *   status? (DRAFT|ACTIVE|ARCHIVED; authorizer_url + public_key required when ACTIVE + COBRANDED).
 *
 * Response also includes:
 *   planned_credit_amount, required_guarantee (= planned × guarantee_percent / 100),
 *   reserve: { id, account_id, currency, balance, held, available, shell_available },
 *   credit_summary: {
 *     total_limit, running_credit, utilization_rate,
 *     collateralized_rate, unsecured_rate, net_exposure,
 *     granted_limit, reserve_held, planned_credit_amount, issuer_account_count
 *   },
 *   card_totals: { total_cards, active_cards, activation_rate, approval_rate, chargeback_ratio },
 *   issuances_count (= card_totals.total_cards),
 *   program_reserve_account_id, webhook_secret_configured,
 *   logo_configured / front_art_configured / back_art_configured,
 *   logo_base64 / front_art_base64 / back_art_base64 (detail; omitted on list).
 *
 * External id: cardprog_{guid:N}.
 */

import { baasProxy } from "./baasConsoleClient";
import {
  DEBIT_CREDIT_GUARANTEE_PERCENT,
  DEBIT_CREDIT_SETTLEMENT_LAGS,
  DEFAULT_PRODUCT_DESIGN,
  formatSettlementLags,
  getMastercardVariantConfig,
  getPsProductCodeForDesign,
  isCobrandedProgramKind,
  DEFAULT_PROCESSOR_BRAND,
  DEFAULT_PROCESSOR_CODE,
  DEFAULT_PROCESSOR_ROUTE_LABEL,
  PROGRAM_KIND_NATIVE,
  resolveMastercardVariantFromPsProductCode,
} from "../Pages/Private/BaasConsole/Processing/cardProgramDesignConstants";

const ISO4217_NUMERIC_TO_ALPHA = {
  "986": "BRL",
  "840": "USD",
  "978": "EUR",
};

const normalizeStatus = (status) => {
  const raw = String(status || "")
    .trim()
    .toUpperCase();
  if (raw === "ACTIVE") return "active";
  if (raw === "ARCHIVED") return "suspended";
  return "draft";
};

const resolveGuaranteeCurrency = (reserve) => {
  const code = String(reserve?.currency || "986").trim();
  if (/^[A-Za-z]{3}$/.test(code)) return code.toUpperCase();
  return ISO4217_NUMERIC_TO_ALPHA[code] || "BRL";
};

/**
 * Build a displayable data URL from raw base64 + content type.
 * @param {string | null | undefined} base64
 * @param {string | null | undefined} contentType
 */
export const toImageDataUrl = (base64, contentType) => {
  const raw = typeof base64 === "string" ? base64.trim() : "";
  if (!raw) return null;
  if (raw.startsWith("data:")) return raw;
  const ct = String(contentType || "image/png").trim() || "image/png";
  return `data:${ct};base64,${raw}`;
};

/**
 * Persist logo as base64 (data URL) or external http(s) URL.
 * blob: previews are not sent.
 * @param {string | null | undefined} value
 * @returns {{
 *   logoBase64: string | null,
 *   logoUrl: string | null,
 *   logoContentType: string | null,
 *   clear: boolean
 * }}
 */
export const resolvePersistableLogo = (value) => {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) {
    return { logoBase64: null, logoUrl: null, logoContentType: null, clear: true };
  }

  if (raw.startsWith("blob:")) {
    return { logoBase64: null, logoUrl: null, logoContentType: null, clear: false };
  }

  if (raw.startsWith("data:")) {
    const match = /^data:([^;,]+)?(?:;charset=[^;]+)?;base64,/i.exec(raw);
    const logoContentType = match?.[1]?.trim() || "image/png";
    return { logoBase64: raw, logoUrl: null, logoContentType, clear: false };
  }

  try {
    const url = new URL(raw);
    if (url.protocol !== "http:" && url.protocol !== "https:") {
      return { logoBase64: null, logoUrl: null, logoContentType: null, clear: false };
    }
  } catch {
    return { logoBase64: null, logoUrl: null, logoContentType: null, clear: false };
  }

  const lower = raw.toLowerCase();
  let logoContentType = null;
  if (lower.endsWith(".png")) logoContentType = "image/png";
  else if (lower.endsWith(".svg")) logoContentType = "image/svg+xml";
  else if (lower.endsWith(".jpg") || lower.endsWith(".jpeg")) logoContentType = "image/jpeg";
  else if (lower.endsWith(".webp")) logoContentType = "image/webp";

  return { logoBase64: null, logoUrl: raw, logoContentType, clear: false };
};

/**
 * Persist optional front/back art data URL.
 * @param {string | null | undefined} value
 */
export const resolvePersistableArt = (value) => {
  const raw = typeof value === "string" ? value.trim() : "";
  if (!raw) {
    return { artBase64: null, artContentType: null, clear: true };
  }
  if (raw.startsWith("blob:")) {
    return { artBase64: null, artContentType: null, clear: false };
  }
  if (!raw.startsWith("data:")) {
    return { artBase64: null, artContentType: null, clear: false };
  }
  const match = /^data:([^;,]+)?(?:;charset=[^;]+)?;base64,/i.exec(raw);
  return {
    artBase64: raw,
    artContentType: match?.[1]?.trim() || "image/png",
    clear: false,
  };
};

const buildVersoPayload = (design = {}) => ({
  website: design.website?.trim() || null,
  phone_capitals: design.phoneMetropolitan?.trim() || null,
  phone_other: design.phoneOther?.trim() || null,
  card_number_preview: design.cardNumberPreview?.trim() || null,
  expiry_preview: design.expiryPreview?.trim() || null,
  cvc_preview: design.cvcPreview?.trim() || null,
  name_preview: design.cardholderPreviewName?.trim() || null,
});

/**
 * Map BaaS CardProgramResponse → console list/detail view model.
 * @param {object} api
 * @param {object} [instance]
 */
export const mapBaasCardProgramToUi = (api, instance = null) => {
  if (!api) return null;

  const psProductCode = String(api.ps_product_code || api.product?.code || "").trim();
  const mastercardVariant = resolveMastercardVariantFromPsProductCode(psProductCode);
  const variantConfig = getMastercardVariantConfig(mastercardVariant);
  const network = String(api.network || "mastercard")
    .trim()
    .toLowerCase() || "mastercard";
  const status = normalizeStatus(api.status);
  const verso = api.verso || {};
  const reserve = api.reserve || null;
  const guaranteeCurrency = resolveGuaranteeCurrency(reserve);
  const plannedCreditAmount = Number(api.planned_credit_amount ?? 0) || 0;
  const guaranteePercent = Number(api.guarantee_percent ?? DEBIT_CREDIT_GUARANTEE_PERCENT);
  const requiredGuarantee = Number.isFinite(Number(api.required_guarantee))
    ? Number(api.required_guarantee)
    : Number(
        (
          plannedCreditAmount *
          ((Number.isFinite(guaranteePercent) ? guaranteePercent : DEBIT_CREDIT_GUARANTEE_PERCENT) / 100)
        ).toFixed(2),
      );
  // "Valor da garantia" = required collateral for planned credit (not reserve balance).
  const guaranteeAmount = requiredGuarantee;
  const debitDays = Number(api.settlement_debit_days ?? DEBIT_CREDIT_SETTLEMENT_LAGS[0]);
  const creditDays = Number(api.settlement_credit_days ?? DEBIT_CREDIT_SETTLEMENT_LAGS[1]);
  const settlementLags = [debitDays, creditDays];
  const hasAuthorizer = Boolean(String(api.authorizer_url || "").trim());
  const invoiceDays = Array.isArray(api.invoice_days)
    ? api.invoice_days.map((day) => Number(day)).filter((day) => Number.isFinite(day))
    : [];
  const programKind = String(api.program_kind || PROGRAM_KIND_NATIVE)
    .trim()
    .toUpperCase() || PROGRAM_KIND_NATIVE;
  const isCobranded = isCobrandedProgramKind(programKind);
  const processorApi = api.processor || null;
  const processorCode = String(
    api.processor_code || processorApi?.code || DEFAULT_PROCESSOR_CODE,
  )
    .trim()
    .toUpperCase() || DEFAULT_PROCESSOR_CODE;
  const processorRoute = String(
    api.processor_route || processorApi?.route_target || processorCode,
  )
    .trim()
    .toUpperCase() || processorCode;
  const processorBrand =
    processorApi?.brand_name || DEFAULT_PROCESSOR_BRAND;
  const processorRouteLabel =
    processorApi?.route_label || DEFAULT_PROCESSOR_ROUTE_LABEL;
  const processor = {
    code: processorCode,
    // Valut route branding only — never partner catalog name (e.g. Processadora Brasil).
    name: processorBrand || processorRouteLabel,
    brandName: processorBrand,
    country: processorApi?.country || "BR",
    providerKind: processorApi?.provider_kind || null,
    routeTarget: processorRoute,
    routeLabel: processorRouteLabel,
    active: processorApi?.active !== false,
  };

  const logoFromBase64 = toImageDataUrl(api.logo_base64, api.logo_content_type);
  const frontArtFromBase64 = toImageDataUrl(api.front_art_base64, api.front_art_content_type);
  const backArtFromBase64 = toImageDataUrl(api.back_art_base64, api.back_art_content_type);

  const cardTotals = api.card_totals || null;
  const creditSummary = api.credit_summary || null;
  const totalCards = Number(cardTotals?.total_cards ?? api.issuances_count ?? 0) || 0;
  const activeCards = Number(cardTotals?.active_cards ?? 0) || 0;
  const activationRate = Number(cardTotals?.activation_rate ?? 0) || 0;

  return {
    id: api.id,
    cardProgramId: api.card_program_id,
    name: api.name,
    instanceId: api.instance_id ?? api.instanceId ?? instance?.baasInstanceId ?? instance?.id ?? null,
    instanceName:
      api.instance_name ||
      api.instanceName ||
      instance?.displayName ||
      instance?.name ||
      null,
    psProductCode,
    network,
    programKind,
    processorCode,
    processorRoute,
    processor,
    status: api.status,
    active: Boolean(api.active),
    issuancesCount: totalCards,
    cardTotals: {
      totalCards,
      activeCards,
      activationRate,
      approvalRate: Number(cardTotals?.approval_rate ?? 0) || 0,
      chargebackRatio: Number(cardTotals?.chargeback_ratio ?? 0) || 0,
    },
    creditSummary: creditSummary
      ? {
          currency: resolveGuaranteeCurrency({
            currency: creditSummary.currency || guaranteeCurrency,
          }),
          totalLimitBrl: Number(creditSummary.total_limit ?? 0) || 0,
          runningCreditBrl: Number(creditSummary.running_credit ?? 0) || 0,
          utilizationRate: Number(creditSummary.utilization_rate ?? 0) || 0,
          collateralizedRate: Number(creditSummary.collateralized_rate ?? 0) || 0,
          unsecuredRate: Number(creditSummary.unsecured_rate ?? 0) || 0,
          netExposureBrl: Number(creditSummary.net_exposure ?? 0) || 0,
          grantedLimitBrl: Number(creditSummary.granted_limit ?? 0) || 0,
          reserveHeldBrl: Number(creditSummary.reserve_held ?? 0) || 0,
          plannedCreditAmount: Number(creditSummary.planned_credit_amount ?? plannedCreditAmount) || 0,
          issuerAccountCount: Number(creditSummary.issuer_account_count ?? 0) || 0,
        }
      : null,
    plannedCreditAmount,
    requiredGuarantee,
    guaranteeAmount,
    guaranteeCurrency,
    guaranteePercent: Number.isFinite(guaranteePercent) ? guaranteePercent : DEBIT_CREDIT_GUARANTEE_PERCENT,
    programReserveAccountId: api.program_reserve_account_id || reserve?.account_id || null,
    reserve: reserve
      ? {
          id: reserve.id,
          accountId: reserve.account_id || null,
          currency: guaranteeCurrency,
          balance: Number(reserve.balance ?? 0) || 0,
          held: Number(reserve.held ?? 0) || 0,
          available: Number(reserve.available ?? 0) || 0,
          shellAvailable: reserve.shell_available ?? null,
        }
      : null,
    logoConfigured: Boolean(api.logo_configured ?? logoFromBase64 ?? api.logo_url),
    frontArtConfigured: Boolean(api.front_art_configured ?? frontArtFromBase64),
    backArtConfigured: Boolean(api.back_art_configured ?? backArtFromBase64),
    productDesign: {
      ...DEFAULT_PRODUCT_DESIGN,
      name: api.name,
      status,
      network,
      mastercardVariant,
      productType: variantConfig.productType,
      backgroundColor: variantConfig.backgroundColor,
      customLogoUrl: logoFromBase64 || api.logo_url || null,
      frontArtUrl: null,
      backArtUrl: null,
      website: verso.website || api.support_website || DEFAULT_PRODUCT_DESIGN.website,
      phoneMetropolitan:
        verso.phone_capitals || api.support_phone1 || DEFAULT_PRODUCT_DESIGN.phoneMetropolitan,
      phoneOther: verso.phone_other || api.support_phone2 || DEFAULT_PRODUCT_DESIGN.phoneOther,
      cardNumberPreview:
        verso.card_number_preview || DEFAULT_PRODUCT_DESIGN.cardNumberPreview,
      expiryPreview: verso.expiry_preview || DEFAULT_PRODUCT_DESIGN.expiryPreview,
      cvcPreview: verso.cvc_preview || DEFAULT_PRODUCT_DESIGN.cvcPreview,
      cardholderPreviewName:
        verso.name_preview || DEFAULT_PRODUCT_DESIGN.cardholderPreviewName,
    },
    authorizerConfig: {
      provider: isCobranded ? "Client authorizer" : processor.brandName,
      clientUrl: api.authorizer_url || "",
      publicKey: api.authorizer_public_key || "",
      status: isCobranded ? (hasAuthorizer ? "configured" : "pending") : "native",
      rulesCount: 0,
    },
    settlementSchedule: {
      type: formatSettlementLags(settlementLags),
      settlementDays: settlementLags,
      frequency: "daily",
      cutoffTime: null,
      invoiceDays,
    },
    fundingMode: String(api.funding_mode || "linked_account")
      .trim()
      .toLowerCase() || "linked_account",
    fundingAccountId: api.funding_account_id || null,
    config: {
      programKind,
      processorCode,
      authorizerClientUrl: api.authorizer_url || "",
      authorizerPublicKey: api.authorizer_public_key || "",
      invoiceDays,
      settlementDays: settlementLags,
      guaranteePercent: Number.isFinite(guaranteePercent)
        ? guaranteePercent
        : DEBIT_CREDIT_GUARANTEE_PERCENT,
      webhookUrl: api.webhook_url || "",
      webhookSecretConfigured: Boolean(api.webhook_secret_configured),
      fundingMode: String(api.funding_mode || "linked_account")
        .trim()
        .toLowerCase() || "linked_account",
      fundingAccountId: api.funding_account_id || "",
      issuanceEntityType:
        String(api.issuance_entity_type || "pf").trim().toLowerCase() === "pj" ? "pj" : "pf",
    },
    raw: api,
  };
};

/**
 * Build create payload from wizard design + config.
 * @param {{ design: object, config: object, status?: string }} input
 */
export const buildCreateCardProgramRequest = ({ design, config, status = "ACTIVE" } = {}) => {
  const isBlack =
    design?.network === "mastercard" && (design?.mastercardVariant ?? "black") === "black";
  const { logoBase64, logoUrl, logoContentType } = resolvePersistableLogo(design?.customLogoUrl);
  const settlementLags = isBlack ? DEBIT_CREDIT_SETTLEMENT_LAGS : [];
  const guaranteePercent = isBlack
    ? DEBIT_CREDIT_GUARANTEE_PERCENT
    : config?.guaranteePercent ?? DEBIT_CREDIT_GUARANTEE_PERCENT;

  const body = {
    name: String(design?.name || "").trim(),
    ps_product_code: getPsProductCodeForDesign(design),
    network: design?.network || "mastercard",
    verso: buildVersoPayload(design),
    program_kind: String(config?.programKind || PROGRAM_KIND_NATIVE).trim().toUpperCase() || PROGRAM_KIND_NATIVE,
    processor_code:
      String(config?.processorCode || DEFAULT_PROCESSOR_CODE).trim().toUpperCase()
      || DEFAULT_PROCESSOR_CODE,
    authorizer_url: String(config?.authorizerClientUrl || "").trim() || null,
    authorizer_public_key: String(config?.authorizerPublicKey || "").trim() || null,
    funding_mode: String(config?.fundingMode || "linked_account").trim().toLowerCase() || "linked_account",
    issuance_entity_type:
      String(config?.issuanceEntityType || "pf").trim().toLowerCase() || "pf",
    status,
  };

  if (body.funding_mode === "linked_account") {
    const fundingAccountId = String(config?.fundingAccountId || "").trim();
    if (fundingAccountId) body.funding_account_id = fundingAccountId;
  }

  if (logoBase64) {
    body.logo_base64 = logoBase64;
    if (logoContentType) body.logo_content_type = logoContentType;
  } else if (logoUrl) {
    body.logo_url = logoUrl;
    if (logoContentType) body.logo_content_type = logoContentType;
  }

  if (isBlack) {
    body.invoice_days = Array.isArray(config?.invoiceDays) ? config.invoiceDays : [];
    body.settlement_debit_days = settlementLags[0];
    body.settlement_credit_days = settlementLags[1];
    body.guarantee_percent = guaranteePercent;
  }

  const webhookUrl = String(config?.webhookUrl || "").trim();
  if (webhookUrl) {
    body.webhook_url = webhookUrl;
  }

  return body;
};

/**
 * Build PATCH body from product-design form fields.
 * @param {object} productDesign
 * @param {{ omitNameAndLogo?: boolean }} [options]
 *   When true (ACTIVE program), omit name, logo and verso — all locked after approval.
 */
export const buildPatchCardProgramDesignRequest = (
  productDesign = {},
  { omitNameAndLogo = false } = {},
) => {
  if (omitNameAndLogo) {
    return {};
  }

  const body = {
    verso: buildVersoPayload(productDesign),
  };

  const { logoBase64, logoUrl, logoContentType, clear } = resolvePersistableLogo(
    productDesign.customLogoUrl,
  );
  body.name = String(productDesign.name || "").trim() || undefined;

  if (logoBase64) {
    body.logo_base64 = logoBase64;
    if (logoContentType) body.logo_content_type = logoContentType;
  } else if (logoUrl) {
    body.logo_url = logoUrl;
    if (logoContentType) body.logo_content_type = logoContentType;
  } else if (clear) {
    body.logo_url = "";
    body.logo_base64 = "";
  }

  return body;
};

export const fetchBaasCardPrograms = async (instance, options = {}) => {
  if (!instance) return [];

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: "v1/cards/programs",
    signal: options.signal,
  });

  const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
  return list.map((item) => mapBaasCardProgramToUi(item, instance)).filter(Boolean);
};

/**
 * Valut matrix only: GET `v1/cards/peer-programs`. Empty for partners / non-matrix pins.
 */
export const fetchBaasPeerCardPrograms = async (instance, options = {}) => {
  if (!instance) return [];
  try {
    const query = {};
    if (options.instanceId != null && Number.isFinite(Number(options.instanceId))) {
      query.instance_id = Number(options.instanceId);
    }
    const data = await baasProxy({
      instance,
      method: "GET",
      baasPath: "v1/cards/peer-programs",
      query,
      signal: options.signal,
    });
    const list = Array.isArray(data) ? data : Array.isArray(data?.items) ? data.items : [];
    return list.map((item) => mapBaasCardProgramToUi(item, instance)).filter(Boolean);
  } catch (error) {
    if (typeof console !== "undefined" && console.warn) {
      console.warn("[baas] GET /v1/cards/peer-programs failed", error);
    }
    return [];
  }
};

export const fetchBaasCardProgramById = async (instance, programId, options = {}) => {
  if (!instance || !programId) return null;

  const data = await baasProxy({
    instance,
    method: "GET",
    baasPath: `v1/cards/programs/${encodeURIComponent(programId)}`,
    signal: options.signal,
  });

  return mapBaasCardProgramToUi(data, instance);
};

export const createBaasCardProgram = async (instance, { design, config, status } = {}, options = {}) => {
  if (!instance) {
    const err = new Error("BaaS instance is required");
    err.name = "BaasConsoleError";
    err.code = "instance_required";
    throw err;
  }

  const body = buildCreateCardProgramRequest({ design, config, status });
  const data = await baasProxy({
    instance,
    method: "POST",
    baasPath: "v1/cards/programs",
    body,
    signal: options.signal,
  });

  return mapBaasCardProgramToUi(data, instance);
};

export const patchBaasCardProgram = async (instance, programId, patchBody, options = {}) => {
  if (!instance || !programId) {
    const err = new Error("instance and programId are required");
    err.name = "BaasConsoleError";
    err.code = "validation_error";
    throw err;
  }

  const data = await baasProxy({
    instance,
    method: "PATCH",
    baasPath: `v1/cards/programs/${encodeURIComponent(programId)}`,
    body: patchBody,
    signal: options.signal,
  });

  return mapBaasCardProgramToUi(data, instance);
};

export const updateBaasCardProgramDesign = async (instance, programId, productDesign, options = {}) =>
  patchBaasCardProgram(
    instance,
    programId,
    buildPatchCardProgramDesignRequest(productDesign, {
      omitNameAndLogo: Boolean(options.omitNameAndLogo),
    }),
    options,
  );

/**
 * PATCH planned credit volume used to compute required guarantee.
 * @param {object} instance
 * @param {string} programId
 * @param {number|string} plannedCreditAmount
 */
export const updateBaasCardProgramPlannedCredit = async (
  instance,
  programId,
  plannedCreditAmount,
  options = {},
) => {
  const amount = Number(plannedCreditAmount);
  return patchBaasCardProgram(
    instance,
    programId,
    {
      planned_credit_amount: Number.isFinite(amount) && amount >= 0 ? amount : 0,
    },
    options,
  );
};

/** @deprecated Sync mock removed — use fetchBaasCardPrograms. */
export const getMockBaasCardPrograms = (instanceId) => {
  void instanceId;
  return [];
};

/** @deprecated Sync mock removed — use fetchBaasCardProgramById. */
export const getBaasCardProgramById = (programId) => {
  void programId;
  return null;
};

/** @deprecated Use createBaasCardProgram. */
export const createMockBaasCardProgram = () => {
  throw new Error("createMockBaasCardProgram removed — use createBaasCardProgram");
};

/** @deprecated Use updateBaasCardProgramDesign. */
export const updateMockBaasCardProgramDesign = () => {
  throw new Error("updateMockBaasCardProgramDesign removed — use updateBaasCardProgramDesign");
};
